import * as Y from "yjs";

/**
 * Yjs transaction origin taxonomy.
 *
 * All CRDT mutations go through `doc.transact(fn, origin)`. The origin is what
 * downstream consumers (sync filter, Store rebuild observer, Y.UndoManager)
 * use to classify changes. Keep this list as the single source of truth.
 *
 * Semantics:
 *   - `USER_ORIGIN`: user-initiated structural edits. Undoable. Synced.
 *   - `LAYOUT_ORIGIN`: local layout/dimension writes derived from rendering
 *     (mmWidth, mmHeight, mmTextHash). Not undoable. Synced on main doc
 *     only (shared docs route dimensions to a local cache; see
 *     dimension-manager.js).
 *   - `IMPORT_ORIGIN`: fromTree / resetWithTree — replaces the whole tree.
 *     Dangerous. Not undoable. Synced.
 *   - `MIGRATION_ORIGIN`: schema/data migrations (status→tags, notes
 *     fragment, refnotes). Not undoable. Synced.
 *   - `CONSISTENCY_ORIGIN`: mergeTreeAdditive — additive repair when Store
 *     has nodes the CRDT doesn't. Not undoable. Synced.
 *   - `ORPHAN_REPAIR_ORIGIN`: self-heal orphans inside toTree(). Not
 *     undoable. Synced. Triggers no Store rebuild because toTree() has
 *     already re-attached the orphans in-memory.
 *   - `SESSION_ORIGIN`: which account this editing session is
 *     (map-format.js `editSessions`), written after a change, signed. Not
 *     undoable. Synced.
 *   - `REMOTE_ORIGIN`: applyUpdate from the server. Never undoable. Not
 *     re-synced.
 *
 * Non-string origins seen in the wild:
 *   - `{ key: 'y-sync$' }`: y-prosemirror local edits to a Y.XmlFragment.
 *     Treated as local + synced. Its own yUndoPlugin handles undo/redo.
 *   - IndexeddbPersistence instance: local persistence load. Not synced.
 */

export const USER_ORIGIN = 'user';
export const LAYOUT_ORIGIN = 'layout';
export const IMPORT_ORIGIN = 'import';
export const MIGRATION_ORIGIN = 'migration';
export const CONSISTENCY_ORIGIN = 'consistency-repair';
export const ORPHAN_REPAIR_ORIGIN = 'orphan-repair';
export const SESSION_ORIGIN = 'session';
export const REMOTE_ORIGIN = 'remote';

const LOCAL_STRING_ORIGINS = new Set([
  USER_ORIGIN,
  LAYOUT_ORIGIN,
  IMPORT_ORIGIN,
  MIGRATION_ORIGIN,
  CONSISTENCY_ORIGIN,
  ORPHAN_REPAIR_ORIGIN,
  SESSION_ORIGIN,
  'local', // legacy string still in use during migration
]);

/**
 * Is this origin a local change (i.e., should be synced to the server and
 * rebuild the Store synchronously)?
 *
 * Remote updates from the server use `REMOTE_ORIGIN`. IndexeddbPersistence
 * passes its instance as origin on load. Yjs itself uses `null` for default
 * transactions (treat as local).
 *
 * @param {*} origin The raw Yjs transaction origin.
 * @returns {boolean}
 */
export function isLocalTxOrigin(origin) {
  if (origin === REMOTE_ORIGIN) return false;
  if (origin === null || origin === undefined) return true;
  if (typeof origin === 'string') return LOCAL_STRING_ORIGINS.has(origin);
  if (origin && origin.key === 'y-sync$') return true;
  // Y.UndoManager uses itself as the origin when replaying an undo/redo.
  // Those are local edits by this user and must be classified as such so
  // they sync to the server and fire the same Store-rebuild path as a
  // normal user edit. Without this, the view lags behind an undo.
  if (origin instanceof Y.UndoManager) return true;
  return false;
}

/**
 * Is this origin a user-initiated structural edit (tracked by the tree
 * UndoManager)? Narrower than `isLocalTxOrigin`.
 *
 * @param {*} origin
 * @returns {boolean}
 */
export function isUserOrigin(origin) {
  return origin === USER_ORIGIN;
}
